import { Component, Output, EventEmitter, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule, MatMenu } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { RowNode } from 'ag-grid-community';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { Document } from 'app/models/dto/document.model';
import { formatCategory } from '../documents-grid-column-defs';

@Component({
  selector: 'documents-header',
  imports: [
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    MatMenuModule,
    MatCheckboxModule,
    CommonModule,
    FormsModule
  ],
  templateUrl: './documents-header.component.html',
  styleUrls: ['./documents-header.component.scss'],
  animations: [
    trigger('fadeInOut', [
      state('void', style({
        opacity: 0
      })),
      transition('void <=> *', animate(300)),
    ]),
    trigger('rotateIcon', [
      state('normal', style({
        transform: 'rotate(0deg)'
      })),
      state('rotating', style({
        transform: 'rotate(360deg)'
      })),
      transition('normal => rotating', animate('500ms ease-in-out')),
      transition('rotating => normal', animate('0ms'))
    ])
  ]
})
export class DocumentsHeaderComponent {
  @Input() documents: Document[] = [];
  @Input() borrowerDocuments: Document[] = [];
  @Input() selectedRows: RowNode[] = [];
  @Input() currentUser: LenderUser;
  @Input() dealId?: number;
  @Input() isDownloading = false;
  
  @Output() handleUploadEvent = new EventEmitter<any>();
  @Output() handleDownloadEvent = new EventEmitter<any>();
  @Output() attachBorrowerDocumentEvent = new EventEmitter<any>();
  @Output() handleRefreshDocumentsEvent = new EventEmitter<any>();
  
  @ViewChild('documentsTab') documentsTab: MatMenu;
  
  selectedDocuments: Document[] = [];
  refreshIsRotating = false;
  formatCategory = formatCategory;

  // Getter that filters borrowerDocuments to exclude those already in documents
  get availableBorrowerDocuments(): Document[] {
    if (!this.borrowerDocuments || !this.documents) {
      return this.borrowerDocuments || [];
    }
    
    const existingDocumentIds = new Set(this.documents.map(doc => doc.id));
    return this.borrowerDocuments.filter(doc => !existingDocumentIds.has(doc.id));
  }

  // Getter that creates a flat list with category headers as special items for mat menu
  get flattenedBorrowerDocuments(): Array<Document | { isCategory: true, category: string }> {
    const documents = this.availableBorrowerDocuments;
    const result: Array<Document | { isCategory: true, category: string }> = [];
    
    // Group documents by category
    const grouped = documents.reduce((acc, doc) => {
      const category = doc.category || 'Other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(doc);
      return acc;
    }, {} as Record<string, Document[]>);
    
    // Sort categories alphabetically
    const sortedCategories = Object.keys(grouped).sort();
    
    // Create flat list with category headers
    sortedCategories.forEach(category => {
      // Add category header
      result.push({ isCategory: true, category });
      
      // Add documents in this category (sorted alphabetically)
      const sortedDocs = grouped[category].sort((a, b) => a.title.localeCompare(b.title));
      result.push(...sortedDocs);
    });
    
    return result;
  }

  handleUpload() {
    this.handleUploadEvent.emit();
  }

  handleRefreshDocuments() {
    // Trigger rotation animation
    this.refreshIsRotating = true;
    
    // Emit the refresh event
    this.handleRefreshDocumentsEvent.emit();
    
    // Reset rotation state after animation completes
    setTimeout(() => {
      this.refreshIsRotating = false;
    }, 500);
  }

  docSelected(document: Document) {
    return this.selectedDocuments.find(d => d.id === document.id) !== undefined;
  }



  toggleSelected(document: Document, checked: boolean) {
    if (checked) {
      if (!this.docSelected(document)) {
        this.selectedDocuments.push(document);
      }
    } else {
      this.selectedDocuments = this.selectedDocuments.filter(d => d.id !== document.id);
    }
  }

  attachSelected() {
    this.selectedDocuments.forEach(document => {
      this.attachBorrowerDocumentEvent.emit(document);
    });
    this.selectedDocuments = [];
    // Close the menu after attaching
    if (this.documentsTab) {
      this.documentsTab.closed.emit();
    }
  }

  handleDownload($event: Event) {
    this.handleDownloadEvent.emit($event);
  }
}
